import { CertificateExtendedClient } from '@frontend/certificate/api';
import { certificateRequest } from '@frontend/certificate/events';
import { Certificate, CertificateExtended, CertificateType, EquipmentCertificateExtended, UserCertificateExtended } from '@frontend/certificate/types';
import { ClassType, EntityType } from '@frontend/common';
import { StatusBadge } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { CommonMessage } from '@frontend/lang';
import React from 'react';

import { TranslationContainer } from '../../applications/modals/containers/translation-container/translations-container.component';
import { Certified, Expired } from '../../equipment/modal/equipment-detail-sections';
import { PlatformDetailGrid } from '../../modal/platform-detail-grid/platform-detail-grid.component';
import { PlatformObjectBadge } from '../../modal/platform-object-badge/platform-object-badge.component';
import { ReactiveDetailRegistryEntry } from '../../modal/reactive-detail-shell/reactive-detail.types';
import { useAppDispatch } from '../../redux/store';

export const CertificateDetailsCard = ({ certificate }: { certificate: CertificateExtended }) => (
    <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
        <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.DETAILS}</div>
        <PlatformDetailGrid
            object={certificate}
            cols={2}
            order={['code', 'num_linked_entities', 'description']}
            keyOverwrite={
                new Map<string, React.ReactNode | ((obj: CertificateExtended) => React.ReactNode)>([
                    ['code', CommonMessage.OBJECTS.CERTIFICATE.CODE],
                    [
                        'num_linked_entities',
                        (obj: CertificateExtended) => (
                            <div>
                                <span>{CommonMessage.DETAIL.LINKED} </span>
                                <span className='lowercase'>
                                    {obj.type === CertificateType.EQUIPMENT ? CommonMessage.OBJECTS.EQUIPMENT.PLURAL : CommonMessage.OBJECTS.USER.PLURAL}
                                </span>
                            </div>
                        )
                    ]
                ])
            }
        />
    </div>
);

export const CertificateTranslationsSection = ({ certificate }: { certificate: CertificateExtended }) => (
    <TranslationContainer
        object={certificate}
        translations={['description']}
        ContainerTitle={CommonMessage.DETAIL.TRANSLATIONS}
        HeaderList={[CommonMessage.OBJECTS.COMMON_FIELDS.DESCRIPTION]}
    />
);

export const CertificateLinkedEquipmentsSection = ({ certificate }: { certificate: CertificateExtended }) => {
    const dispatch = useAppDispatch();
    return (
        <ReactiveObjectList<EquipmentCertificateExtended>
            fetch={(arg) =>
                CertificateExtendedClient.fetchEquipmentCertificates({ ...arg, account_id: certificate.account_id, certificate_id: certificate.id })
            }
            label={CommonMessage.OBJECTS.EQUIPMENT.PLURAL}
            dispatch={dispatch}
            type={EntityType.EQUIPMENT_CERTIFICATE}
            columns={{
                columns: ['product', 'equipment', 'status', 'to_timestamp'],
                headerOverwrite: new Map([
                    ['product', CommonMessage.OBJECTS.PRODUCT.PRODUCT_NAME],
                    ['equipment', CommonMessage.OBJECTS.EQUIPMENT.DEFAULT],
                    ['to_timestamp', CommonMessage.OBJECTS.CERTIFICATE.EXPIRATION_DATE]
                ]),
                customCells: new Map([
                    [
                        'status',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                <StatusBadge
                                    value={obj.row.original.expired ? Expired : Certified}
                                    colorMap={
                                        new Map([
                                            [[Certified], ClassType.SUCCESS],
                                            [[Expired], ClassType.WARNING]
                                        ])
                                    }
                                />
                            </td>
                        )
                    ],
                    [
                        'equipment',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                <PlatformObjectBadge
                                    entityType={EntityType.EQUIPMENT}
                                    id={obj.row.original.equipment?.entity_id || ''}
                                    display={obj.row.original.equipment?.name}
                                />
                            </td>
                        )
                    ],
                    [
                        'product',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                <PlatformObjectBadge
                                    entityType={EntityType.PRODUCT}
                                    id={obj.row.original.product?.entity_id || ''}
                                    display={obj.row.original.product?.name}
                                />
                            </td>
                        )
                    ]
                ])
            }}
            tableProps={{ defaultHeight: true, hideFooter: true }}
        />
    );
};

export const CertificateLinkedUsersSection = ({ certificate }: { certificate: CertificateExtended }) => {
    const dispatch = useAppDispatch();
    return (
        <ReactiveObjectList<UserCertificateExtended>
            fetch={(arg) => CertificateExtendedClient.fetchUserCertificates({ ...arg, account_id: certificate.account_id, certificate_id: certificate.id })}
            label={CommonMessage.OBJECTS.USER.PLURAL}
            dispatch={dispatch}
            type={EntityType.USER_CERTIFICATE}
            columns={{
                columns: ['user', 'status', 'to_timestamp'],
                headerOverwrite: new Map([
                    ['user', CommonMessage.OBJECTS.USER.DEFAULT],
                    ['to_timestamp', CommonMessage.OBJECTS.CERTIFICATE.EXPIRATION_DATE]
                ]),
                customCells: new Map([
                    [
                        'status',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                <StatusBadge
                                    value={obj.row.original.expired ? Expired : Certified}
                                    colorMap={
                                        new Map([
                                            [[Certified], ClassType.SUCCESS],
                                            [[Expired], ClassType.WARNING]
                                        ])
                                    }
                                />
                            </td>
                        )
                    ],
                    [
                        'user',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                <PlatformObjectBadge
                                    entityType={EntityType.USER}
                                    id={obj.row.original.user?.entity_id || ''}
                                    display={obj.row.original.user?.name}
                                />
                            </td>
                        )
                    ]
                ])
            }}
            tableProps={{ defaultHeight: true, hideFooter: true }}
        />
    );
};

export const CERTIFICATE_LINKED_EQUIPMENT_LABEL = 'Linked equipments';
export const CERTIFICATE_LINKED_USERS_LABEL = 'Linked users';

export const certificateReactiveDetail: ReactiveDetailRegistryEntry<Certificate, CertificateExtended> = {
    entityType: EntityType.CERTIFICATE,
    resolveFunction: certificateRequest,
    fetchExtended: ({ object }) => CertificateExtendedClient.fetchCertificate(object.account_id, object.id),
    modalClassName: 'w-full lg:w-2/3 max-w-[900px]',
    title: ({ object, extended }) => (
        <>
            {CommonMessage.OBJECTS.CERTIFICATE.DEFAULT}: {extended?.code ?? object.code}
        </>
    ),
    tabs: [
        {
            id: 'general',
            label: CommonMessage.DETAIL.GENERAL,
            render: (view) =>
                view.extended ? (
                    <>
                        <CertificateDetailsCard certificate={view.extended} />
                        <CertificateTranslationsSection certificate={view.extended} />
                    </>
                ) : null
        },
        {
            id: 'linked',
            label: CERTIFICATE_LINKED_EQUIPMENT_LABEL,
            visible: (view) => view.extended?.type === CertificateType.EQUIPMENT,
            render: (view) => (view.extended ? <CertificateLinkedEquipmentsSection certificate={view.extended} /> : null)
        },
        {
            id: 'linked-users',
            label: CERTIFICATE_LINKED_USERS_LABEL,
            visible: (view) => view.extended?.type === CertificateType.USER,
            render: (view) => (view.extended ? <CertificateLinkedUsersSection certificate={view.extended} /> : null)
        }
    ]
};
